/**
 * Enhanced Product Pop Logic
 * Secure and performant product preview popups
 */

(function() {
  'use strict';

  let productCache = new Map();
  let activePopup = null;
  let hoverTimeout = null;

  function init() {
    const productPops = document.querySelectorAll('[data-with-product]');
    if (productPops.length === 0) return;

    productPops.forEach(setupProductPop);
  }

  function setupProductPop(element) {
    const productHandle = element.dataset.withProduct;
    const direction = element.dataset.direction || 'next';
    
    if (!productHandle) return;

    // Hover events with delay
    element.addEventListener('mouseenter', () => {
      hoverTimeout = setTimeout(() => {
        showProductPop(element, productHandle, direction);
      }, 300);
    });

    element.addEventListener('mouseleave', () => {
      clearTimeout(hoverTimeout);
      hideProductPop();
    });

    // Touch events for mobile
    element.addEventListener('touchstart', (e) => {
      e.preventDefault();
      showProductPop(element, productHandle, direction);
    }, { passive: false });
  }

  async function showProductPop(element, productHandle, direction) {
    try {
      const productData = await getProductData(productHandle);
      if (!productData) return;

      hideProductPop(); // Hide any existing popup

      const popup = createPopup(productData, direction);
      positionPopup(popup, element, direction);
      
      document.body.appendChild(popup);
      activePopup = popup;

      // Animate in
      requestAnimationFrame(() => {
        popup.classList.add('show');
      });

    } catch (error) {
      console.error('Product pop failed:', error);
    }
  }

  function hideProductPop() {
    if (activePopup) {
      activePopup.classList.remove('show');
      setTimeout(() => {
        if (activePopup && activePopup.parentNode) {
          activePopup.parentNode.removeChild(activePopup);
        }
        activePopup = null;
      }, 200);
    }
  }

  async function getProductData(handle) {
    // Check cache first
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const product = await response.json();
      
      // Cache the product data
      productCache.set(handle, product);
      
      return product;
    } catch (error) {
      console.error(`Failed to fetch product ${handle}:`, error);
      return null;
    }
  }

  function createPopup(product, direction) {
    const popup = document.createElement('div');
    popup.className = `product-popup product-popup-${direction}`;
    
    const variant = product.variants[0];
    const image = product.images[0];
    const imageUrl = image ? image.src.replace(/\.(jpg|png|webp)/, '_300x.$1') : '';
    
    popup.innerHTML = `
      <div class="product-popup-content">
        <div class="product-popup-image">
          <img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy">
          ${product.compare_at_price > product.price ? 
            `<span class="sale-badge">-${Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)}%</span>` 
            : ''}
        </div>
        <div class="product-popup-details">
          <h3 class="product-popup-title">${escapeHtml(product.title)}</h3>
          <div class="product-popup-price">
            ${product.compare_at_price > product.price ? 
              `<span class="old-price">${formatMoney(product.compare_at_price)}</span>` 
              : ''}
            <span class="current-price">${formatMoney(product.price)}</span>
          </div>
          ${product.available ? 
            '<span class="availability in-stock">In Stock</span>' : 
            '<span class="availability out-stock">Out of Stock</span>'}
        </div>
        <div class="product-popup-arrow"></div>
      </div>
    `;

    return popup;
  }

  function positionPopup(popup, element, direction) {
    const rect = element.getBoundingClientRect();
    const popupWidth = 280;
    const popupHeight = 120;
    
    let left, top;
    
    if (direction === 'prev') {
      left = rect.left - popupWidth - 10;
      top = rect.top + (rect.height / 2) - (popupHeight / 2);
    } else {
      left = rect.right + 10;
      top = rect.top + (rect.height / 2) - (popupHeight / 2);
    }
    
    // Ensure popup stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (left < 10) left = 10;
    if (left + popupWidth > viewportWidth - 10) left = viewportWidth - popupWidth - 10;
    if (top < 10) top = 10;
    if (top + popupHeight > viewportHeight - 10) top = viewportHeight - popupHeight - 10;
    
    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  // Add styles
  function addStyles() {
    if (document.getElementById('product-pop-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'product-pop-styles';
    styles.textContent = `
      .product-popup {
        position: fixed;
        z-index: 9999;
        opacity: 0;
        transform: scale(0.9);
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: none;
      }

      .product-popup.show {
        opacity: 1;
        transform: scale(1);
      }

      .product-popup-content {
        background: white;
        border-radius: 8px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
        padding: 16px;
        width: 280px;
        display: flex;
        gap: 12px;
        position: relative;
        border: 1px solid rgba(0, 0, 0, 0.08);
      }

      .product-popup-image {
        position: relative;
        flex-shrink: 0;
      }

      .product-popup-image img {
        width: 80px;
        height: 80px;
        object-fit: cover;
        border-radius: 6px;
      }

      .sale-badge {
        position: absolute;
        top: -4px;
        right: -4px;
        background: #e74c3c;
        color: white;
        font-size: 10px;
        padding: 2px 4px;
        border-radius: 3px;
        font-weight: 600;
      }

      .product-popup-details {
        flex: 1;
        min-width: 0;
      }

      .product-popup-title {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 8px 0;
        line-height: 1.3;
        color: #222529;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .product-popup-price {
        margin-bottom: 6px;
      }

      .old-price {
        font-size: 12px;
        color: #999;
        text-decoration: line-through;
        margin-right: 6px;
      }

      .current-price {
        font-size: 16px;
        font-weight: 700;
        color: #222529;
      }

      .availability {
        font-size: 11px;
        font-weight: 500;
      }

      .availability.in-stock {
        color: #27ae60;
      }

      .availability.out-stock {
        color: #e74c3c;
      }

      .product-popup-arrow {
        position: absolute;
        width: 8px;
        height: 8px;
        background: white;
        border: 1px solid rgba(0, 0, 0, 0.08);
        transform: rotate(45deg);
      }

      .product-popup-prev .product-popup-arrow {
        right: -5px;
        top: 50%;
        margin-top: -4px;
        border-left: none;
        border-top: none;
      }

      .product-popup-next .product-popup-arrow {
        left: -5px;
        top: 50%;
        margin-top: -4px;
        border-right: none;
        border-bottom: none;
      }

      /* Mobile adjustments */
      @media (max-width: 767px) {
        .product-popup-content {
          width: 260px;
          padding: 12px;
        }

        .product-popup-image img {
          width: 60px;
          height: 60px;
        }

        .product-popup-title {
          font-size: 13px;
        }

        .current-price {
          font-size: 14px;
        }
      }

      /* Hide on very small screens */
      @media (max-width: 480px) {
        .product-popup {
          display: none;
        }
      }
    `;

    document.head.appendChild(styles);
  }

  // Initialize
  document.addEventListener('DOMContentLoaded', () => {
    addStyles();
    init();
  });

  // Handle page navigation
  document.addEventListener('click', (e) => {
    if (e.target.closest('.product-nav a')) {
      hideProductPop();
    }
  });

  // Hide popup on scroll
  let scrollTimeout;
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(hideProductPop, 100);
  }, { passive: true });

  // Expose for external use
  window.ProductPopEnhanced = { init, hideProductPop };
})();